import * as React from "react";

import { IWatchdog } from "utils/watchdog";
import GetWatchdogsApiEndpoint from "utils/watchdog/api/GetWatchdogs";
import ActivateWatchdogApiEndpoint from "utils/watchdog/api/ActivateWatchdog";
import DeleteWatchdogApiEndpoint from "utils/watchdog/api/DeleteWatchdog";
import useApiEndpoint from "./useApiEndpoint";
import useGlobalState from "./useGlobalState";
import useRouter from "./useRouter";
import useHelperCRM, { TEventDataWatchdogCreated } from "./useHelperCRM";
import { resolveDogFreq } from "helpers/watchdog";
import { useTracking } from "contexts/Tracking";
import { sendWatchdogConversion } from "helpers/conversions";
import CreateWatchdogApiEndpoint, {
  TCreateWatchDogInput,
} from "utils/watchdog/api/CreateWatchdog";

interface IWatchdogStateAndActions {
  getWatchdogs: (onWds?: (wds: Array<IWatchdog>) => void) => void;
  activateDog: (id: number, isSearch?: boolean) => void;
  deleteDog: (
    watchdogId: number,
    onDelete: () => void,
    isSearch?: boolean,
  ) => void;
  createDog: (
    wdData: TCreateWatchDogInput,
    onSubmit?: (() => void) | undefined,
  ) => void;
  setEdit: React.Dispatch<React.SetStateAction<IWatchdog | null>>;
  edit: null | IWatchdog;
  watchdogs: Array<IWatchdog>;
}

export default function useWatchdogs(): IWatchdogStateAndActions {
  const { user } = useGlobalState();
  const { navigateTo } = useRouter();
  const { trackEvent } = useTracking();
  const { createEventWatchdogCreated } = useHelperCRM();

  const { callEndpoint: getWatchdogsApi } = useApiEndpoint(
    GetWatchdogsApiEndpoint,
  );
  const { callEndpoint: activateWatchdogApi } = useApiEndpoint(
    ActivateWatchdogApiEndpoint,
  );
  const { callEndpoint: deleteWatchdogApi } = useApiEndpoint(
    DeleteWatchdogApiEndpoint,
  );
  const { callEndpoint: createWatchdogApi } = useApiEndpoint(
    CreateWatchdogApiEndpoint,
  );

  const [wds, setWds] = React.useState<Array<IWatchdog>>([]);
  const [edit, setEdit] = React.useState<null | IWatchdog>(null);

  React.useEffect(() => {
    if (!user) {
      setWds([]);
      setEdit(null);
    }
  }, [user]);

  const getAllWatchdogs = React.useCallback(
    (onWds?: (wds: Array<IWatchdog>) => void): void => {
      getWatchdogsApi().then(({ data }) => {
        if (data) {
          setWds(data);
          onWds && onWds(data);
        }
      });
    },
    [getWatchdogsApi],
  );

  const activateWatchdog = React.useCallback(
    (id: number, isSearch?: boolean) => {
      activateWatchdogApi({ id }).then(() => {
        if (isSearch) {
          window.location.reload();
        } else {
          getAllWatchdogs();
        }
      });
    },
    [activateWatchdogApi, getAllWatchdogs],
  );

  const deleteWatchdog = React.useCallback(
    (watchdogId: number, onDelete: () => void, isSearch?: boolean) =>
      deleteWatchdogApi({ watchdogId }).then(() => {
        onDelete && onDelete();
        if (isSearch) {
          navigateTo("/hlidaci-pes");
        } else {
          getAllWatchdogs();
        }
      }),
    [deleteWatchdogApi, getAllWatchdogs, navigateTo],
  );

  const createWatchdog = React.useCallback(
    async (
      wdData: TCreateWatchDogInput,
      onSubmit?: (() => void) | undefined,
    ) => {
      const res = await createWatchdogApi({
        ...wdData,
      });

      if (res.data?.status === "ok") {
        const dispositionsId = wdData.filter.dispositions.map(
          (item) => item.translationId,
        );
        const parseData: TEventDataWatchdogCreated = {
          city: wdData.location,
          dispositions: dispositionsId,
          furnishing: [],
          frequency: wdData.frequencyId,
          noCommission: false,
        };
        createEventWatchdogCreated({
          userEmail: wdData.email,
          eventData: parseData,
        });

        onSubmit && onSubmit();

        sendWatchdogConversion("landing");

        const frequency = resolveDogFreq(wdData.frequencyId);
        trackEvent({
          "dl.eCat": "watchdog",
          "dl.eAct": "vytvoreni_strankapsa",
          "dl.eLab": frequency,
        });

        const { data } = await getWatchdogsApi();
        if (data) {
          setWds(data);
        }
      }
    },
    [
      createEventWatchdogCreated,
      createWatchdogApi,
      getWatchdogsApi,
      trackEvent,
    ],
  );

  const actions: IWatchdogStateAndActions = {
    getWatchdogs: getAllWatchdogs,
    activateDog: activateWatchdog,
    deleteDog: deleteWatchdog,
    createDog: createWatchdog,
    setEdit,
    edit,
    watchdogs: wds,
  };

  return actions;
}
